import React, { useEffect, useState } from "react";
import { ActivityCard, ChoiceButton, MixedText, PlayButton, ProgressPill, useShuffledChoices } from "../shared";

/**
 * ObjectSwap — "Make It One Word" noun-to-pronoun replacement rounds.
 *
 * Each item shows a sentence with its object noun highlighted; the student
 * picks the single verb+pronoun word that swallows it (choices are authored
 * correct-first and shuffled per item). A correct pick reveals the swapped
 * sentence with the pronoun suffix highlighted, plus a teaching note; wrong
 * picks shake off so the item can be retried. Completes after the last item.
 */

/** The sentence with its object noun colored. */
function ObjectSentence({ item }) {
  const [before, after] = item.sentence_ar.split(item.object_ar);
  return (
    <>
      {before}
      <span style={{ color: "var(--tag-violet)" }}>{item.object_ar}</span>
      {after}
    </>
  );
}

/** The swapped sentence with the pronoun suffix colored inside the answer word. */
function SwappedSentence({ item }) {
  const word = item.choices[item.correct_index];
  const [before, after] = item.swapped_ar.split(word);
  const stem = word.slice(0, word.length - item.suffix_ar.length);
  return (
    <>
      {before}
      {stem}
      <span style={{ color: "var(--gold-bright)" }}>{item.suffix_ar}</span>
      {after}
    </>
  );
}

export default function ObjectSwap({ data, onComplete, audio }) {
  const [itemIdx, setItemIdx] = useState(0);
  const [picked, setPicked] = useState(null); // shuffled index of current pick
  const [revealed, setRevealed] = useState(false);
  const [finished, setFinished] = useState(false);

  const items = data.items;
  const item = items[itemIdx];
  const view = useShuffledChoices(item.choices, item.correct_index, [item]);

  useEffect(() => {
    if (finished) onComplete();
  }, [finished, onComplete]);

  const handlePick = (idx) => {
    if (picked !== null || revealed) return;
    setPicked(idx);
    if (idx === view.correctIndex) {
      setRevealed(true);
      audio.play(item.swapped_ar);
    } else {
      // Wrong guess: shake it off and let them try this one again.
      setTimeout(() => setPicked(null), 900);
    }
  };

  const next = () => {
    if (itemIdx + 1 >= items.length) {
      setFinished(true);
      return;
    }
    setItemIdx((i) => i + 1);
    setPicked(null);
    setRevealed(false);
  };

  if (finished) {
    return (
      <ActivityCard>
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
          <span className="text-lg leading-none" aria-hidden="true">🪄</span>
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={data.done_message} />
          </p>
        </div>
      </ActivityCard>
    );
  }

  return (
    <ActivityCard>
      <div className="flex items-center justify-between gap-3 mb-5">
        <ProgressPill>Sentence {itemIdx + 1}/{items.length}</ProgressPill>
        {audio.audioAllowed && (
          <PlayButton text={item.sentence_ar} playingText={audio.playingText} onPlay={audio.play} />
        )}
      </div>

      <div className="rounded-xl px-4 pt-3 pb-3 mb-2 text-center" style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}>
        <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.9] m-0" style={{ color: "var(--parchment)" }}>
          <ObjectSentence item={item} />
        </p>
        <p className="text-sm mt-1 mb-0" style={{ color: "var(--parchment-dim)" }}>{item.sentence_en}</p>
      </div>

      <p className="text-sm text-center mb-4" style={{ color: "var(--parchment-dim)" }}>
        Say it in one word — <span style={{ color: "var(--tag-violet)" }}>{item.object_en}</span> rides the verb:
      </p>

      <div className="grid grid-cols-2 gap-2.5 max-w-md mx-auto">
        {view.choices.map((choice, idx) => {
          let state = "idle";
          if (revealed) {
            state = idx === view.correctIndex ? "correct" : "dim";
          } else if (picked !== null) {
            state = idx === picked ? "wrong" : "dim";
          }
          return (
            <ChoiceButton
              key={idx}
              arabic
              state={state}
              disabled={revealed || picked !== null}
              onClick={() => handlePick(idx)}
            >
              {choice}
            </ChoiceButton>
          );
        })}
      </div>

      {revealed && (
        <div className="mt-5 rounded-xl px-4 pt-3 pb-3 text-center" style={{ background: "var(--violet-bg)", border: "1px solid var(--panel-line)" }}>
          <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.9] m-0" style={{ color: "var(--parchment)" }}>
            <SwappedSentence item={item} />
          </p>
          <p className="text-sm mt-1 mb-2" style={{ color: "var(--parchment-dim)" }}>{item.swapped_en}</p>
          <p className="text-sm m-0" style={{ color: "var(--parchment)" }}>
            <MixedText text={item.note} />
          </p>
          <div className="flex justify-center mt-4">
            <button
              type="button"
              onClick={next}
              className="inline-flex items-center justify-center px-5 py-2.5 min-h-[44px] text-sm font-semibold rounded-lg transition-[filter] hover:brightness-105"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              {itemIdx + 1 >= items.length ? "Finish" : "Next sentence"}
            </button>
          </div>
        </div>
      )}
    </ActivityCard>
  );
}
